import React, { useState, useEffect, useRef } from 'react';
import withSearchBar from 'global/components/withSearchBar';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, flatten, size } from 'global/lib/lodash';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { List, Button, Icon } from '@mui/material';
import Row, { RowHeader } from './row';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { withRouter } from 'react-router-dom';
import { buildFilters } from 'global/utils/filter';
import { CoursesSelectedContainer, CoursesButtonContainer } from './styles';
import useDebounce from 'global/hooks/useDebounce';
import SnackAlerts from 'global/components/snack-alerts';
import { scrollTop, _size } from 'global/utils';
import permissionGate from 'global/components/permission-gate';
import { FilterChips } from 'global/components/filterchips';
import setFilter from 'redux/actions/setFilter';
import setUserRealm from 'redux/actions/userRealm';
import { useUser } from 'global/hooks/useUser';
import { CourseModal } from './modal';
import { EditorModal } from './editormodal';
import NavToTop from 'feature/nav/NavToTop';

const pageSize = 100;

export default permissionGate(
  withRouter(
    withSearchBar((props) => {
      const [selected, setSelected] = useState({});
      const [alerts, setAlerts] = useState();
      const [modal, setModal] = useState();
      const [editorModal, setEditorModal] = useState();
      const page = useRef(1);
      const filter = useSelector((state) => get(state, 'filter.users.courses'));
      const debouncedSearch = useDebounce(filter.searchstring, 1000);
      const [filtChips, setFiltChips] = useState([]);
      const dispatch = useDispatch();
      const me = useUser();

      const filterProps = () => {
        const filts = buildFilters(filter, {
          dontFilter: [
            'client_id',
            // 'status',
            'realm',
          ],
          convertName: {
            searchstring: 'search',
            realm: 'parent_id',
          },
        });
        if (get(filts, 'filter.status') === 'inactive') {
          set(filts, 'filter.onlyTrashed', 1);
        }

        filtChips.map((f) => {
          set(filts, `filter.${f.filter}`, f.value);
        });

        set(filts, 'filter.status', undefined);

        return filts;
      };

      const buildParams = () => {
        return [
          { url: `/course-reports` },
          {
            build: ['courseReports'],
            useEffect: true,
            params: {
              ...filterProps(),
              page: { size: pageSize, number: page.current },
              include: 'realm,reportable,parent,children,editionAssignments,userAssignments',
            },
            transformData: (data) => data.filter((d) => d.reportableType == filter.report_type),
          },
        ];
      };

      const [userParams, setUserParams] = useState(buildParams());

      const courses = useRetrieveData(...userParams);

      useEffect(() => {
        if (courses.error) {
          setAlerts({ message: courses.error, severity: 'error' });
        } else {
          setAlerts();
        }
      }, [courses.error]);

      useEffect(() => {
        page.current = 1;
        setUserParams(buildParams());
      }, [filter, debouncedSearch, me.siteContextSelectedRealm, filtChips]);

      useEffect(() => {
        //individual report_type filter changes
        if (filter.report_type !== 'user' && !!size(filtChips)) {
          setFiltChips([]);
        }
        dispatch(setFilter('users', 'courses', { ...filter, searchstring: '' }));
      }, [filter.report_type]);

      const lastPage = get(courses.meta, 'page.last-page', null);
      const updatePage = (newPage) => {
        if (newPage <= lastPage) {
          page.current = newPage;
          setUserParams(buildParams());
        }
      };

      const onSelectAll = () => {
        if (Object.keys(selected).length > 0) {
          setSelected({});
        } else {
          const obj = {};
          const umap = courses.data;
          umap.map((du) => (obj[du.id] = du));
          setSelected(obj);
        }
      };

      const onSelectItem = (p) => {
        const sel = { ...selected };
        const { item } = p;
        const exists = get(selected, item.id);
        if (exists) {
          delete sel[item.id];
        } else {
          set(sel, item.id, item);
        }
        setSelected(sel);
      };

      const onSelectRow = (e, p) => {
        const { item } = p;

        // if (filter.report_type === 'realm') {
        //   dispatch(setUserRealm(get(p, 'item.reportable')));
        // }

        if (filter.report_type !== 'user') {
          setFiltChips([
            { name: get(item, 'reportable.name'), filter: filter.report_type === 'edition' ? `${filter.report_type}_id` : 'user_realm_id', value: item.reportableId },
            { name: get(item, 'realm.name'), filter: 'realm_id', value: get(item, 'realm.id') },
          ]);

          dispatch(
            setFilter('users', 'courses', {
              ...filter,
              report_type: 'user',
              searchstring: '',
            })
          );
        }
      };

      const onDeleteChip = (d) => {
        if (d.filter === 'realm_id') {
          dispatch(setUserRealm(null));
        }

        setFiltChips(filtChips.filter((item) => item.filter !== d.filter));
      };

      const toggleModal = (startScene = 0, opts = {}) => {
        setModal({
          open: true,
          selected,
          startScene,
          onClose: async (shouldUpdate, created) => {
            setModal();
            if (shouldUpdate) {
              setSelected({});
              const newData = await courses.update();

              const hasAss = flatten((created || []).map((c) => c.createdAssignments)).find((f) => !!f.assessment);
              if (created && hasAss) {
                const createdarr = created.map((c) => get(c, 'user.reportable.id'));
                const newsel = {};
                newData.map((d) => {
                  if (createdarr.indexOf(`${d.reportableId}`) > -1) {
                    newsel[d.id] = d;
                  }
                });

                if (_size(newsel)) {
                  toggleEditorModal(0, { selected: newsel });
                }
              }
            }
          },
          ...opts,
        });
      };

      const toggleEditorModal = (startScene = 0, opts = {}) => {
        setEditorModal({
          open: true,
          selected,
          startScene,
          onClose: (shouldUpdate) => {
            setEditorModal();
            if (shouldUpdate) {
              setSelected({});
              courses.update();
            }
          },
          ...opts,
        });
      };

      const hasSelected = !!_size(selected);

      return (
        <>
          <CoursesSelectedContainer>
            <CoursesButtonContainer>
              {hasSelected && (
                <Button className="accessButton" onClick={() => toggleModal()} variant="contained" color={'secondary'} startIcon={<Icon>add</Icon>}>
                  ADD/REMOVE COURSES
                </Button>
              )}
              {hasSelected && filter.report_type === 'user' && (
                <Button className="accessButton" onClick={() => toggleEditorModal()} variant="contained" color={'secondary'} startIcon={<Icon>supervised_user_circle</Icon>}>
                  CHANGE REVIEWERS
                </Button>
              )}
              {!hasSelected && (
                <Button className="accessButton" onClick={() => toggleModal(-1)} variant="contained" color={'secondary'} startIcon={<Icon>add</Icon>}>
                  ADD USERS TO COURSES
                </Button>
              )}
            </CoursesButtonContainer>
            <FilterChips filters={filtChips} onDelete={onDeleteChip} />
          </CoursesSelectedContainer>

          <NavToTop onClick={() => scrollTop()} />

          <ListWithPages
            handlePageChange={updatePage}
            data={courses.data}
            loading={courses.loading}
            header={<RowHeader filter={filter} selectAll={onSelectAll} />}
            selected={selected}
            onSelectItem={onSelectItem}
            onSelectRow={onSelectRow}
            currentPage={page.current}
            filter={filter}
            filtChips={filtChips}
          />

          <CourseModal {...modal} />
          <EditorModal {...editorModal} />

          <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
        </>
      );
    })
  )
);

const ListWithPages = withInfiniteScroll(Row, List);
